import {isNil, isNotNil} from 'ramda';

/** Signed URLs live for hours: an image failing this soon after its response is a broken file. */
const FRESH_RESPONSE_MS = 60_000;
/** The storage token's default lifetime: a URL signed after a recovery cannot expire before it ends. */
const SIGNED_URL_LIFETIME_MS = 4 * 60 * 60 * 1000;

type ImageOwnerQuery = {
  originalArgs?: unknown;
  fulfilledTimeStamp?: number;
  refetch: () => unknown;
};

/**
 * When each owning query last refetched for its images: per hook, keyed by `refetch`, which RTK
 * keeps stable for the hook's lifetime while every read renews the URLs, then per argument, as one
 * hook can switch between owners whose URLs expire independently. Two hooks on one cache entry,
 * such as an edit dialog and its table, keep separate budgets: at most one redundant refetch per
 * expiry, which is cheaper than passing each call site's endpoint name to key by cache entry.
 */
const recoveredAt = new WeakMap<() => unknown, Map<string, number>>();

/**
 * The image `onError` for signed URLs that came in `query`'s response. A failure on an older
 * response refetches it for fresh URLs, once per URL lifetime; a failure on a fresh response, or
 * on the URLs a recovery brought, stays a placeholder, so a broken file is never retried in a loop.
 */
export const refetchOnImageExpiry = (query: ImageOwnerQuery) => () => {
  const now = Date.now();
  const recoveries = recoveredAt.get(query.refetch) ?? new Map<string, number>();
  const owner = JSON.stringify(query.originalArgs);
  const lastRecovery = recoveries.get(owner);
  if (
    isNil(query.fulfilledTimeStamp) ||
    now - query.fulfilledTimeStamp <= FRESH_RESPONSE_MS ||
    (isNotNil(lastRecovery) && now - lastRecovery < SIGNED_URL_LIFETIME_MS)
  ) {
    return;
  }
  recoveredAt.set(query.refetch, recoveries.set(owner, now));
  void query.refetch();
};
